import { Blur } from './blur';
import { SFU_KINDS } from '../../../../shared/protocol';
// 영상·음성·화면 공유 전담. 쌍마다 RTCPeerConnection(메시) 하나, 트랜시버 셋(마이크·카메라·화면)을 미리 두고
// 켜고 끄기는 replaceTrack만 쓴다(재협상 없음). 연결을 열고 닫는 쪽은 id가 작은 쪽, 큰 쪽은 받기만 한다. 게임 규칙은 모른다.
export const AV = (() => {
  const KIND = SFU_KINDS;          // 트랜시버 순서 (audio, video, video)
  const RETRY_MS = 3000, CONNECT_MS = 20000, SIG_GAP = 25;   // sig는 서버가 초당 50회까지라 25ms 간격으로 흘려 보낸다
  const conns = new Map(), retries = new Map(), early = [], names = new Map();
  const ext = new Map();   // 직접 연결이 아닌 곳(SFU)에서 받은 트랙: id → { mic, cam, share: MediaStream|null, audio, shareAt }. 칸·패널은 직접 연결 트랙과 똑같이 그린다
  const local = { mic: null, cam: null, share: null };       // 내 MediaStreamTrack
  const api = { state: { mic: false, cam: false, share: false }, onChange: null, blur: true, loading: false, error: '' };   // blur: 배경 흐림 켜짐, loading: 흐림 준비 중, error: 사용자에게 보일 마지막 실패 안내
  let camRaw = null, camPipe = null, camTok = 0, camBusy = 0, camSource = null;   // camRaw: 장치에서 받은 원본 카메라 트랙, camPipe: 흐림 파이프라인
  let myId = '', sendRaw = null, ice = [], want = new Set(), outbox = [], outTimer = 0;
  // 보내기 정책(게임이 정한다): send(id, kind) → 그 상대에게 내 mic|cam|share를 보내는가, show(id) → 영상 칸을 보이는가, rank(id) → 공유 패널 우선순위(클수록 먼저),
  // hear(id) → 그 사람이 직접 연결로 보내는 소리·영상을 받아들이는가(강사가 있는 교실에서 말할 자격이 없는 사람이 직접 연결로 보내도 듣지 않는다)
  let policy = { send: () => true, show: () => true, rank: () => 0, hear: () => true };
  const ask = (f, ...a) => { try { return f(...a); } catch (e) { return f === policy.rank ? 0 : true; } };

  // ---------- DOM ----------
  // 영상 칸 줄(#avStrip)은 public/index.html에 있고 모양도 거기서 정한다(게임처럼 어두운 판, 크림 테두리, 갈무리11). 여기서는 칸을 넣고 뺄 뿐이다.
  // 공유 화면을 칠판에 보이는 일은 3단계: 공유 트랙은 받지만 아직 어디에도 그리지 않는다
  const mkEl = (tag, props?, ...kids) => { const e = Object.assign(document.createElement(tag), props); e.append(...kids); return e; };
  const strip = document.getElementById('avStrip');

  function mkTile(me) {
    const v = mkEl('video', { autoplay: true, playsInline: true, muted: true, hidden: true }), nm = mkEl('span', { className: 'avName' }), dot = mkEl('span', { className: 'avDot', hidden: true }), hand = mkEl('span', { className: 'avHand', hidden: true, textContent: '손' });
    const el = mkEl('div', { className: 'avTile' + (me ? ' me' : '') }, v, nm, dot, hand);
    return { el, v, nm, dot, hand };
  }
  const meTile = mkTile(true); strip.appendChild(meTile.el); meTile.el.hidden = true;
  const info = id => names.get(id) || {};
  function paint(t, id, cam, mic) {
    t.nm.textContent = id === myId ? '나' : info(id).name || '…';
    t.el.style.setProperty('--c', info(id).color || '#7fd08a');
    t.el.classList.toggle('cam', !!cam); t.v.hidden = !cam; t.dot.hidden = !mic; t.hand.hidden = !info(id).hand;   // hand: 그 사람이 손을 들었다(같은 모임일 때)
  }
  function renderMe() {
    meTile.el.hidden = !(local.cam || conns.size || ext.size);
    paint(meTile, myId, local.cam, local.mic);
    const src = local.cam ? new MediaStream([local.cam]) : null;
    if (!local.cam) meTile.v.srcObject = null; else if (!meTile.v.srcObject || meTile.v.srcObject.getTracks()[0] !== local.cam) { meTile.v.srcObject = src; meTile.v.play().catch(() => {}); }
  }
  // 그 사람에게서 받는 카메라·공유 영상: 직접 연결로 보낸다고 알린 것이 먼저(상대가 SFU를 그만두고 직접 연결로 돌아온 경우), 없으면 SFU에서 온 것
  const ex = id => ext.get(id) || {};
  const camOf = (id, c = conns.get(id)) => (c && c.st.cam && c.cam && ask(policy.hear, id) && c.cam) || ex(id).cam || null;
  const shareOf = (id, c = conns.get(id)) => (c && c.st.share && c.share) || ex(id).share || null;
  const paintConn = c => {
    const cam = camOf(c.id, c);
    c.audio.muted = !ask(policy.hear, c.id);
    if (c.tile.v.srcObject !== cam) { c.tile.v.srcObject = cam; if (cam) c.tile.v.play().catch(() => {}); }
    paint(c.tile, c.id, cam, c.st.mic || !!ex(c.id).mic); c.tile.el.hidden = !ask(policy.show, c.id);
  };
  // 직접 연결 없이 서버 송출(SFU)로만 받는 사람(학교에서 직접 연결 상한을 넘은 사람)의 칸
  const xTiles = new Map();
  function paintExt(id) {
    const c = conns.get(id), e = ext.get(id);
    if (c || !e) { const t = xTiles.get(id); if (t) { t.el.remove(); t.v.srcObject = null; xTiles.delete(id); } if (c) paintConn(c); return; }
    let t = xTiles.get(id); if (!t) { t = mkTile(false); xTiles.set(id, t); strip.appendChild(t.el); }
    const cam = e.cam || null;
    if (t.v.srcObject !== cam) { t.v.srcObject = cam; if (cam) t.v.play().catch(() => {}); }
    paint(t, id, cam, !!e.mic); t.el.hidden = false;
  }
  // ---------- 소개 메시지 송신 ----------
  function send(to, data) {
    outbox.push([to, data]);
    if (outTimer) return;
    outTimer = setInterval(() => { const m = outbox.shift(); if (m && sendRaw) sendRaw(m[0], m[1]); if (!outbox.length) { clearInterval(outTimer); outTimer = 0; } }, SIG_GAP);
  }
  const stMsg = c => ({ st: { mic: +!!c.sent[0], cam: +!!c.sent[1], share: +!!c.sent[2] } });   // 그 상대에게 실제로 보내는 것만 알린다(트랙이 아니라 메시지로)

  // ---------- 연결 ----------
  function mkConn(id) {
    const pc = new RTCPeerConnection({ iceServers: ice }), tile = mkTile(false), audio = new Audio();
    audio.autoplay = true;
    const c = { id, pc, tile, audio, ice: [], st: { mic: 0, cam: 0, share: 0 }, shareAt: 0, cam: null, share: null, timer: 0, sent: [] };   // sent: 트랜시버별로 지금 보내는 트랙(정책 반영)
    conns.set(id, c); strip.appendChild(tile.el);
    pc.onicecandidate = e => { if (e.candidate) send(id, { ice: e.candidate.toJSON() }); };
    pc.ontrack = e => {
      const i = pc.getTransceivers().indexOf(e.transceiver), ms = new MediaStream([e.track]);
      if (i === 0) { audio.srcObject = ms; audio.play().catch(() => {}); }
      else if (i === 1) { c.cam = ms; paintConn(c); }
      else if (i === 2) { c.share = ms; }
    };
    pc.onconnectionstatechange = () => {
      if (conns.get(id) !== c) return;
      const s = pc.connectionState;
      if (s === 'connected') { clearTimeout(c.timer); send(id, stMsg(c)); }
      else if (s === 'failed' || s === 'closed') fail(c);
    };
    c.timer = setTimeout(() => { if (conns.get(id) === c && pc.connectionState !== 'connected') fail(c); }, CONNECT_MS);
    paintConn(c); renderMe();
    return c;
  }
  function destroy(c) {
    if (conns.get(c.id) === c) conns.delete(c.id);
    clearTimeout(c.timer); c.pc.onicecandidate = c.pc.ontrack = c.pc.onconnectionstatechange = null; c.pc.close();
    c.tile.el.remove(); c.tile.v.srcObject = null; c.audio.srcObject = null;
    paintExt(c.id);   // 서버 송출로 받는 중이면 그 칸은 남긴다
    renderMe();
  }
  function later(id) { clearTimeout(retries.get(id)); retries.set(id, setTimeout(() => { retries.delete(id); reconcile(); }, RETRY_MS)); }
  function fail(c) { const id = c.id; destroy(c); send(id, { bye: 1 }); if (id > myId) later(id); }   // 작은 id 쪽이 3초 뒤 다시 연다
  // 정책에 맞춰 각 sender에 내 트랙(허용) 또는 null을 넣는다. 바뀐 게 있으면 true
  function fill(c) {
    let changed = false;
    KIND.forEach((k, j) => {
      const t = c.pc.getTransceivers()[j]; if (!t) return;
      const w = local[k] && ask(policy.send, c.id, k) ? local[k] : null;
      if (c.sent[j] === w) return;
      c.sent[j] = w; changed = true; t.sender.replaceTrack(w).catch(() => {});
    });
    return changed;
  }
  function applyPolicy() { conns.forEach(c => { if (fill(c)) send(c.id, stMsg(c)); paintConn(c); }); }
  const flush = c => c.ice.splice(0).forEach(d => c.pc.addIceCandidate(d).catch(() => {}));
  const sdpOf = d => ({ type: d.type, sdp: d.sdp });

  async function open(id) {
    const c = mkConn(id), pc = c.pc;
    KIND.forEach(k => pc.addTransceiver(k === 'mic' ? 'audio' : 'video', { direction: 'sendrecv' }));
    fill(c);
    await pc.setLocalDescription(await pc.createOffer());
    send(id, { sdp: sdpOf(pc.localDescription) });
  }
  async function onSdp(from, sdp) {
    if (sdp.type === 'offer') {
      const old = conns.get(from); if (old) destroy(old);
      const c = mkConn(from), pc = c.pc;
      await pc.setRemoteDescription(sdp);
      pc.getTransceivers().forEach(t => { t.direction = 'sendrecv'; });
      fill(c);
      await pc.setLocalDescription(await pc.createAnswer());
      send(from, { sdp: sdpOf(pc.localDescription) }); flush(c);
    } else if (sdp.type === 'answer') {
      const c = conns.get(from); if (!c || c.pc.signalingState !== 'have-local-offer') return;
      await c.pc.setRemoteDescription(sdp); flush(c);
    }
  }
  function onSig(from, d) {
    if (!myId) { if (early.length < 50) early.push([from, d]); return; }   // init 전에 온 메시지는 init 때 처리한다
    if (!d || typeof d !== 'object') return;
    if (d.bye) { const c = conns.get(from); if (c) { destroy(c); if (from > myId) later(from); } return; }
    if (d.sdp) { onSdp(from, d.sdp).catch(e => { console.warn('[av]', e); const c = conns.get(from); if (c) fail(c); }); return; }
    const c = conns.get(from); if (!c) return;
    if (d.ice) { if (c.pc.remoteDescription) c.pc.addIceCandidate(d.ice).catch(() => {}); else c.ice.push(d.ice); }
    else if (d.st) { if (d.st.share && !c.st.share) c.shareAt = Date.now(); c.st = { mic: +!!d.st.mic, cam: +!!d.st.cam, share: +!!d.st.share }; paintConn(c); }
  }
  function reconcile() {
    if (!myId) return;
    for (const id of want) if (id > myId && !conns.has(id) && !retries.has(id)) open(id).catch(e => { console.warn('[av]', e); const c = conns.get(id); if (c) fail(c); });
    for (const c of [...conns.values()]) if (c.id > myId && !want.has(c.id)) { destroy(c); send(c.id, { bye: 1 }); }
    applyPolicy();
  }

  // ---------- 내 마이크·카메라·화면 ----------
  function setLocal(kind, track) {
    const old = local[kind]; local[kind] = track; api.state[kind] = !!track;
    if (old && old !== track) { old.onended = null; if (old !== camRaw) old.stop(); }   // 원본 카메라 트랙은 흐림을 껐다 켜도 살려 둔다
    if (track) track.onended = () => { if (local[kind] === track) kind === 'cam' ? offCam() : setLocal(kind, null); };   // 장치 분리·브라우저 「공유 중지」
    conns.forEach(c => { if (fill(c)) send(c.id, stMsg(c)); });
    renderMe(); if (api.onChange) api.onChange();
  }
  const GET = {
    mic: () => navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }),
    cam: () => camSource ? camSource() : navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 30 } } }),
    share: () => navigator.mediaDevices.getDisplayMedia({ video: true }),
  };
  // ---------- 카메라: 원본 트랙 → (배경 흐림이면 Blur 파이프라인) → 보내는 트랙 ----------
  const BLUR_FAIL = '배경 흐림을 불러오지 못해 카메라를 껐습니다. 카메라를 다시 켜면 흐림 없이 켜지고, 🌫 버튼으로 다시 켤 수 있습니다';
  function stopCam() {
    if (camPipe) { camPipe.stop(); camPipe = null; }
    if (camRaw) { camRaw.onended = null; camRaw.stop(); camRaw = null; }
  }
  function offCam() { camTok++; api.loading = false; stopCam(); setLocal('cam', null); }
  const blurFailed = e => { console.warn('[av] blur', e); api.error = BLUR_FAIL; api.blur = false; offCam(); };
  async function setCam(on) {
    if (!on) { offCam(); return false; }
    if (local.cam || (camBusy && camBusy === camTok)) return true;
    const tok = ++camTok, live = () => tok === camTok; camBusy = tok; api.error = '';
    try {
      let raw; try { raw = (await GET.cam()).getTracks()[0]; } catch (e) { return false; }   // 권한 거부·취소
      if (!raw) return false;
      if (!live()) { raw.stop(); return false; }
      camRaw = raw; raw.onended = offCam;
      if (!api.blur) { setLocal('cam', raw); return true; }
      api.loading = true; if (api.onChange) api.onChange();
      let p; try { p = await Blur.start(raw); } catch (e) { if (live()) blurFailed(e); return false; }
      if (!live()) { p.stop(); return false; }
      camPipe = p; setLocal('cam', p.track); return true;   // 흐림이 준비되기 전에는 원본을 보내지 않는다
    } finally { if (live()) api.loading = false; if (camBusy === tok) camBusy = 0; if (api.onChange) api.onChange(); }
  }
  // 카메라가 켜져 있는 동안 흐림을 켜고 끈다. 카메라가 꺼져 있으면 다음에 켤 때 적용할 값만 바꾼다
  async function setBlur(on) {
    api.blur = on; api.error = '';
    if (!camRaw || on === !!camPipe || (camBusy && camBusy === camTok)) { if (api.onChange) api.onChange(); return; }
    const tok = ++camTok, live = () => tok === camTok; camBusy = tok;
    try {
      if (!on) { const old = camPipe; camPipe = null; setLocal('cam', camRaw); camRaw.onended = offCam; old.stop(); return; }
      api.loading = true; if (api.onChange) api.onChange();
      let p; try { p = await Blur.start(camRaw); } catch (e) { if (live()) blurFailed(e); return; }
      if (!live()) { p.stop(); return; }
      camPipe = p; setLocal('cam', p.track); camRaw.onended = offCam;
    } finally { if (live()) api.loading = false; if (camBusy === tok) camBusy = 0; if (api.onChange) api.onChange(); }
  }
  async function toggle(kind, on) {
    if (!on) { setLocal(kind, null); return false; }
    if (local[kind]) return true;
    try {
      const t = (await GET[kind]()).getTracks()[0];
      if (!t) return false;
      if (local[kind]) { t.stop(); return true; }
      setLocal(kind, t); return true;
    } catch (e) { return false; }   // 권한 거부·취소
  }

  return Object.assign(api, {
    init({ myId: id, sendSig, iceServers }) { myId = id; sendRaw = sendSig; ice = iceServers || []; early.splice(0).forEach(([f, d]) => onSig(f, d)); renderMe(); reconcile(); },
    setPeers(ids) { want = new Set(ids); reconcile(); },
    wantedIds() { return [...want]; },   // 지금 연결하려는 상대 id (시험이 링크 규칙을 본다)
    // 보내기 정책: { send(id, kind), show(id), rank(id) } (빠진 것은 기본값). 바꾸면 바로, 그리고 setPeers 때마다 다시 맞춘다
    setPolicy(p) { policy = { send: () => true, show: () => true, rank: () => 0, hear: () => true, ...p }; applyPolicy(); },
    localTrack: kind => local[kind] || null,
    remoteShare: id => shareOf(id),   // 그 사람이 나에게 보내는 공유 화면 (SFU 또는 직접 연결)
    // 그 사람의 kind 트랙을 직접 연결 밖(SFU)에서 받았다(track) / 닫혔다(null)
    setExternal(id, kind, track) {
      if (!KIND.includes(kind)) return;
      let e = ext.get(id);
      if (!e) { if (!track) return; ext.set(id, e = { mic: null, cam: null, share: null, audio: Object.assign(new Audio(), { autoplay: true }), shareAt: 0 }); }
      e[kind] = track ? new MediaStream([track]) : null;
      if (kind === 'mic') { e.audio.srcObject = e.mic; if (e.mic) e.audio.play().catch(() => {}); }
      if (kind === 'share' && track) e.shareAt = Date.now();
      if (!e.mic && !e.cam && !e.share) ext.delete(id);
      paintExt(id); renderMe();
    },
    sending(id) { const c = conns.get(id); return c ? { mic: !!c.sent[0], cam: !!c.sent[1], share: !!c.sent[2] } : null; },
    peerConn: id => (conns.get(id) || {}).pc || null,   // 시험용
    onSig,
    setNames(map) { names.clear(); for (const [id, v] of Object.entries(map)) names.set(id, typeof v === 'string' ? { name: v } : v); conns.forEach(paintConn); [...xTiles.keys()].forEach(paintExt); renderMe(); },
    setMic: on => toggle('mic', on), setCam, setBlur, setShare: on => toggle('share', on),
    // 그 사람이 방을 떠났다: 연결을 바로 지운다 (큰 id 쪽은 bye를 못 받으니)
    drop(id) { const c = conns.get(id); if (c) destroy(c); clearTimeout(retries.get(id)); retries.delete(id); want.delete(id); },
    // 시험용: 다음 setCam(true)가 카메라 대신 이 함수가 돌려주는 MediaStream을 쓴다 (null이면 실제 카메라)
    testCamSource(fn) { camSource = fn; },
    // 시험용: 실제 장치 없이 만든 트랙(canvas.captureStream 등)을 내 마이크·카메라·화면으로 쓴다. kind: 'mic'|'cam'|'share'
    useTrack(kind, track) { if (KIND.includes(kind)) setLocal(kind, track); },
    peerState(id) {   // 그 사람에게서 받는 것 (직접 연결 + SFU)
      const c = conns.get(id), e = ext.get(id);
      if (!e) return c ? c.st : null;
      const st = c ? c.st : { mic: 0, cam: 0, share: 0 };
      return { mic: +!!(st.mic || e.mic), cam: +!!(st.cam || e.cam), share: +!!(st.share || e.share) };
    },
    status() { return Object.fromEntries([...conns].map(([id, c]) => [id, c.pc.connectionState])); },
    reset() {
      [...conns.values()].forEach(destroy); retries.forEach(clearTimeout); retries.clear();
      ext.forEach(e => { e.audio.srcObject = null; }); ext.clear(); [...xTiles.keys()].forEach(paintExt);
      want = new Set(); early.length = 0; outbox = []; clearInterval(outTimer); outTimer = 0; myId = ''; sendRaw = null;
      offCam(); KIND.forEach(k => { if (local[k]) setLocal(k, null); }); renderMe();
    },
  });
})();
